import React from 'react';
import { useAIPanel } from '../../context/AIPanelContext';
import { Bot, BarChart3, FileSearch, Sparkles, Minus, X, Maximize2 } from 'lucide-react';
import ActivityCreatorPanel from './ActivityCreatorPanel';
import AnalyticsAIPanel from './AnalyticsAIPanel';
import DocumentAIPanel from './DocumentAIPanel';
import toast from 'react-hot-toast';

// ============================================
// AI Panel Manager
// ============================================
// Renders all active AI panels as overlays.
// Shows minimized panel dock bar at bottom.
// Persists across page navigation.
// ============================================

const PANEL_ICONS = {
    main: Bot,
    creator: Sparkles,
    analytics: BarChart3,
    document: FileSearch,
};

const PANEL_LABELS = {
    main: 'AI Assistant',
    creator: 'Activity Creator',
    analytics: 'Analytics AI',
    document: 'Document AI',
};

const PANEL_COLORS = {
    main: 'bg-primary',
    creator: 'bg-blue-500',
    analytics: 'bg-indigo-500',
    document: 'bg-cyan-600',
};

const AIPanelManager = () => {
    const {
        activePanels,
        minimizedPanels,
        closePanel,
        minimizePanel,
        restorePanel,
        openCreator,
    } = useAIPanel();

    return (
        <>
            {/* Active (open) panels */}
            {activePanels.map(panel => {
                switch (panel.type) {
                    case 'creator':
                        return (
                            <ActivityCreatorPanel
                                key={panel.id}
                                creatorType={panel.props.creatorType}
                                prefilledData={panel.props.prefilledData}
                                onClose={() => closePanel(panel.id)}
                                onCreated={() => {
                                    closePanel(panel.id);
                                    toast.success(`${panel.props.creatorType} created successfully`);
                                }}
                            />
                        );

                    case 'analytics':
                        return (
                            <AnalyticsAIPanel
                                key={panel.id}
                                isOpen={true}
                                onClose={() => closePanel(panel.id)}
                                initialQuestion={panel.props.initialQuestion}
                            />
                        );

                    case 'document':
                        return (
                            <DocumentAIPanel
                                key={panel.id}
                                isOpen={true}
                                onClose={() => closePanel(panel.id)}
                                file={panel.props.file}
                                allFiles={panel.props.allFiles}
                                onRouteToCreator={(type, data) => {
                                    closePanel(panel.id);
                                    openCreator(type, data);
                                }}
                            />
                        );

                    default:
                        return null;
                }
            })}

            {/* Minimized Panel Dock Bar */}
            {minimizedPanels.length > 0 && (
                <div className="fixed bottom-4 right-4 z-40 flex items-center gap-2 animate-fade-in">
                    {minimizedPanels.map(panel => {
                        const Icon = PANEL_ICONS[panel.type] || Bot;
                        const label = PANEL_LABELS[panel.type] || 'AI Panel';
                        const color = PANEL_COLORS[panel.type] || 'bg-primary';

                        return (
                            <div
                                key={panel.id}
                                className="flex items-center gap-2 px-3 py-2 bg-card border border-border rounded-xl shadow-lg hover:shadow-xl transition-all cursor-pointer group"
                                onClick={() => restorePanel(panel.id)}
                            >
                                <div className={`w-8 h-8 ${color} rounded-lg flex items-center justify-center text-white`}>
                                    <Icon size={16} />
                                </div>
                                <span className="text-sm font-medium text-foreground">{label}</span>
                                <div className="flex items-center gap-0.5 ml-1">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); restorePanel(panel.id); }}
                                        className="p-1 rounded hover:bg-muted transition-colors cursor-pointer"
                                        title="Restore"
                                    >
                                        <Maximize2 size={12} className="text-muted-foreground" />
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); closePanel(panel.id); }}
                                        className="p-1 rounded hover:bg-red-100 transition-colors cursor-pointer"
                                        title="Close"
                                    >
                                        <X size={12} className="text-muted-foreground hover:text-red-500" />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </>
    );
};

export default AIPanelManager;
